import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Bot,
  FileText,
  Home,
  MessageSquare,
  Plus,
  Search,
  Settings2,
  Upload,
  Play,
  Code2,
  BarChart3,
  Globe,
  Database,
  Zap,
  Building2,
  CreditCard,
  Shield,
  Users,
  UserPlus,
} from "lucide-react";

import { NavMain } from "@/components/nav-main";
import { NavProjects } from "@/components/nav-projects";
import { NavUser } from "@/components/nav-user";
import { TeamSwitcher } from "@/components/team-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useBots } from "@/hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";

export function AppSidebar({ ...props }) {
  const { t } = useTranslation();
  const { user, tenants, isAuthenticated, currentTenant } = useAuth();
  const { data: bots, isLoading, error } = useBots();
  const location = useLocation();
  const { id: botId } = useParams();

  // Extract botId from URL if useParams doesn't work (due to nested routes)
  const extractedBotId = React.useMemo(() => {
    const match = location.pathname.match(/^\/bots\/([a-f0-9]{24})(?:\/|$)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Use extracted botId as fallback
  const finalBotId = botId || extractedBotId;

  // Ensure bots is always an array
  const botsArray = Array.isArray(bots) ? bots : [];

  // Find current bot if we're in a bot-specific route
  const currentBot = finalBotId
    ? botsArray.find((bot) => bot._id === finalBotId)
    : null;
  const isBotRoute =
    location.pathname.startsWith("/bots/") &&
    finalBotId &&
    finalBotId !== "new";

  // Debug logging
  console.log("Debug AppSidebar:", {
    botId,
    extractedBotId,
    finalBotId,
    location: location.pathname,
    botsArray: botsArray.map((bot) => ({ id: bot._id, name: bot.name })),
    currentBot: currentBot
      ? { id: currentBot._id, name: currentBot.name }
      : null,
    isBotRoute,
  });

  // Main navigation for organization-level features
  const navMain = [
    {
      title: t("common.dashboard", "Dashboard"),
      url: "/home",
      icon: Home,
      isActive: location.pathname === "/home",
    },
    {
      title: t("common.organization", "Organization"),
      url: "/organization",
      icon: Building2,
      isActive: location.pathname === "/organization",
    },
    {
      title: t("billing.title", "Abbonamento"),
      url: "/billing",
      icon: CreditCard,
      isActive: location.pathname === "/billing",
    },
  ];

  // Administration navigation (only for system admins)
  const adminNavigation =
    user?.type === "admin"
      ? [
          {
            title: t("nav.clients", "Clients"),
            url: "/admin",
            icon: Shield,
            isActive: location.pathname.startsWith("/admin"),
          },
        ]
      : [];

  // Bot-specific navigation (only shown when a bot is selected)
  const botNavigation = currentBot ? [] : [];

  // Convert bots to projects format for the sidebar
  const projects = botsArray.map((bot) => ({
    _id: bot._id,
    name: bot.name,
    url: `/bots/${bot._id}`,
    icon: Bot,
    status: bot.status,
    sourceCount: bot.sourceCount,
    lastIngestion: bot.last_ingest_at,
    isActive: finalBotId === bot._id,
  }));

  // User data for the sidebar
  const userData = {
    name: user?.name || t("nav.defaultUser", "User"),
    email: user?.email || "user@example.com",
    avatar: user?.avatar || "",
    origin: user?.origin,
    currentTenant:
      currentTenant?.name ||
      t("nav.defaultOrganization", "Default Organization"),
  };

  // Only show sidebar content if authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent>
        {/* Main organization-level navigation */}
        <NavMain items={navMain} />

        {/* Administration section (only for system admins) */}
        {adminNavigation.length > 0 && (
          <NavMain
            sectionTitle={t("nav.administration", "Administration")}
            items={adminNavigation}
          />
        )}

        {/* Bot-specific navigation (only when a bot is selected) */}
        {botNavigation.length > 0 && <NavMain items={botNavigation} />}

        {/* List of user's bots */}
        <NavProjects projects={projects} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
